// src/App.jsx
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { ProtectedRoute } from "./components/ProtectedRoute";

import CarList from "./pages/UsedCars";
import CarDetail from "./pages/CarDetail";
import FindCar from "./pages/FindCar";

import Login from "./pages/auth/Login";
import SignupPage from "./pages/auth/Signup";

import AdminLayout from "./pages/admin/AdminLayout";
import VehiclesAdminPage from "./pages/admin/VehiclesAdmin";
import ImagesAdminPage from "./pages/admin/ImagesAdmin";
import RequestsAdmin from "./pages/admin/RequestsAdmin";
import CompareCars from "./pages/CompareCars";
import ProfilePage from "./pages/ProfilePage";
import Delivery from "./pages/Delivery";
import AboutUs from "./pages/AboutUs";
// import CarCollections from "./pages/CarCollections";
import WhichCarShouldIbuy from "./pages/WhichCarShouldIbuy";
import OurCommitment from "./pages/OurCommitment";
import ScrollToTop from "./components/ScrollToTop";
import EmailSettingsAdmin from "./pages/admin/EmailSettings";
import ContactUsPage from "./pages/ContactUs";
import UsersAdmin from "./pages/admin/UserManagement";
import ModernSlaveryStatement from "./pages/policies/ModernSlaveryStatement";
import CookiePolicy from "./pages/policies/CookiePolicy";
import PrivacyPolicy from "./pages/policies/PrivacyPolicy";
import TermsOfUse from "./pages/policies/TermsOfUse";
import ModernSlaveryStatementPage from "./pages/policies/ModernSlaveryStatementPage";
import FeatureCatalogAdmin from "./pages/admin/FeatureCatalog";
import FlexPriceAdmin from "./pages/admin/FlexPriceAdmin";
import { ROLE_PERMISSIONS } from "./data/roles";
import CookieManager from "./components/home/CookieManager";
import QualityStandards from "./pages/QualityStandards";
import NinetyDaysDayWarranty from "./pages/90DayWarranty";
import FindYourPerfectCar from "./pages/FindYourPerfectCar";
import WarrantyAdmin from "./pages/admin/WarrantyAdmin";
import WarrantyTransactionsAdmin from "./pages/admin/WarrantyTransactionsAdmin";
import OrdersAdmin from "./pages/admin/OrdersAdmin";
import CheckoutSuccess from "./pages/CheckoutSuccess";

export default function App() {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (loading) return null;

  // hide header/footer on /admin/* or on /signin & /signup
  const isAdminRoute = location.pathname.startsWith("/admin");
  const authPaths = ["/signin", "/signup"];
  const isAuthRoute = authPaths.includes(location.pathname);
  const showLayout = !isAdminRoute && !isAuthRoute;

  const ADMIN_ORDER = [
    "vehicles",
    "feature-catalog",
    "images",
    "requests",
    "users",
    "email-settings",
    "flex-prices",
  ];
  // only build grants if we have a user
  const grants = new Set(
    (user?.roles || []).flatMap((r) => ROLE_PERMISSIONS[r] || [])
  );
  const defaultAdminTab =
    ADMIN_ORDER.find((key) => grants.has(key)) || "vehicles";

  return (
    <div className="flex flex-col min-h-screen">
      {showLayout && <Header />}

      <ScrollToTop />
      <div className="flex-1">
        <Routes>
          {/* ─── ADMIN AREA ───────────────────────────── */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute
                allowedRoles={[
                  "super-admin",
                  "car-admin",
                  "image-admin",
                  "enquiry-admin",
                ]}
              >
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={defaultAdminTab} replace />} />

            <Route
              path="vehicles"
              element={
                <ProtectedRoute allowedRoles={["super-admin", "car-admin"]}>
                  <VehiclesAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="images"
              element={
                <ProtectedRoute allowedRoles={["super-admin", "image-admin"]}>
                  <ImagesAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="requests"
              element={
                <ProtectedRoute allowedRoles={["super-admin", "enquiry-admin"]}>
                  <RequestsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="email-settings"
              element={
                <ProtectedRoute allowedRoles={["super-admin"]}>
                  <EmailSettingsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="feature-catalog"
              element={
                <ProtectedRoute allowedRoles={["super-admin", "car-admin"]}>
                  <FeatureCatalogAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="flex-prices"
              element={
                <ProtectedRoute allowedRoles={["super-admin", "car-admin"]}>
                  <FlexPriceAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="users"
              element={
                <ProtectedRoute allowedRoles={["super-admin"]}>
                  <UsersAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="warranty"
              element={
                <ProtectedRoute allowedRoles={["super-admin"]}>
                  <WarrantyAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="orders"
              element={
                <ProtectedRoute allowedRoles={["super-admin"]}>
                  <OrdersAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="warranty-transactions"
              element={
                <ProtectedRoute allowedRoles={["super-admin"]}>
                  <WarrantyTransactionsAdmin />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* ─── PUBLIC AUTH ──────────────────────────── */}
          <Route path="/signin" element={<Login />} />
          <Route path="/signup" element={<SignupPage />} />

          {/* ─── PUBLIC SITE ──────────────────────────── */}
          <Route index element={<FindCar />} />
          <Route path="/used-cars" element={<CarList />} />
          <Route path="/car/:carId" element={<CarDetail />} />
          <Route path="/compare" element={<CompareCars />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="/delivery" element={<Delivery />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUsPage />} />

          {/* <Route path="/car-collections" element={<CarCollections />} />/ */}
          <Route
            path="/which-car-should-i-buy"
            element={<WhichCarShouldIbuy />}
          />
          <Route path="/quality-standards" element={<QualityStandards />} />
          <Route
            path="/help-me-choose/most-important"
            element={<FindYourPerfectCar />}
          />
          <Route path="/90-day-warranty" element={<NinetyDaysDayWarranty />} />
          <Route path="/our-commitment" element={<OurCommitment />} />

          {/* Policies pages */}
          <Route path="/policies/terms-of-use" element={<TermsOfUse />} />
          <Route path="/policies/privacy" element={<PrivacyPolicy />} />
          <Route path="/policies/cookie-policy" element={<CookiePolicy />} />
          <Route
            path="/policies/modern-slavery-statement"
            element={<ModernSlaveryStatement />}
          />

          {/* your existing dynamic year route */}
          <Route
            path="/policies/modern-slavery-statement/:year"
            element={<ModernSlaveryStatementPage />}
          />

          <Route path="/checkout/success" element={<CheckoutSuccess />} />

          {/* ─── CATCH-ALL ────────────────────────────── */}
          <Route path="*" element={<p>Page not found</p>} />
        </Routes>
        <CookieManager />
      </div>

      {showLayout && <Footer />}
    </div>
  );
}
